import React, { useState } from 'react';
import { Box, Collapse, Divider, Skeleton, Typography } from '@mui/material';
import { Tag, TagType } from '@juno/client-api/model';
import { FilterSideChip } from './FilterSideChip';
import { MAX_FILTERS_LIMIT } from './types';

interface TagFiltersProps {
  tags: Tag[];
  tagTypes: TagType[];
  selectedFilters: Tag[];
  appendFilter: (tag: Tag) => void;
  removeFilter: (tag: Tag) => void;
  isLoadingTags: boolean;
  isLoadingTagTypes: boolean;
  disabled?: boolean;
  totalFilterApplied: number;
}

export const TagsFilter: React.FC<TagFiltersProps> = ({
  tags,
  tagTypes,
  selectedFilters,
  appendFilter,
  removeFilter,
  isLoadingTags,
  isLoadingTagTypes,
  disabled,
  totalFilterApplied,
}) => {
  const [expandedTagTypes, setExpandedTagTypes] = useState<string[]>([]);
  const [tagsWithHeight, setTagsWithHeight] = useState<string[]>([]);

  const handleRef = (id: string) => (element: HTMLDivElement | null) => {
    if (element && element.scrollHeight > 106 && !tagsWithHeight.includes(id)) {
      setTagsWithHeight((prev) => [...prev, id]);
    }
  };

  if (isLoadingTagTypes) {
    return <TagTypesSkeleton />;
  }

  if (!isLoadingTags && !isLoadingTagTypes && (!tagTypes.length || !tags.length)) {
    return null;
  }

  const isSelected = (tag: Tag) => selectedFilters.some((f) => f.id === tag.id);

  return (
    <Box>
      {tagTypes.map((tt) => {
        if (isLoadingTags) {
          return <TagsSkeleton />;
        }

        const tagsOfType = tags.filter((t) => t.type.id === tt.id);
        if (!tagsOfType.length) return null;

        return (
          <Box
            key={tt.id}
            sx={{
              my: 1,
              pb: 1,
            }}
          >
            <Typography variant='subtitle1' sx={{ px: 1 }}>
              {selectedFilters.filter((t) => t.type.id === tt.id).length
                ? `${selectedFilters.filter((t) => t.type.id === tt.id).length} `
                : ''}
              {tt.value}
            </Typography>
            <Collapse collapsedSize={95} in={expandedTagTypes.includes(tt.id)}>
              <Box p={1} ref={handleRef(tt.id)}>
                {tagsOfType.map((tag) => (
                  <FilterSideChip
                    label={tag.value}
                    key={tag.id}
                    isSelected={isSelected(tag)}
                    disabled={
                      disabled || (totalFilterApplied >= MAX_FILTERS_LIMIT && !isSelected(tag))
                    }
                    onClick={() => {
                      if (isSelected(tag)) {
                        removeFilter(tag);
                      } else {
                        appendFilter(tag);
                      }
                    }}
                  />
                ))}
              </Box>
            </Collapse>
            {tagsWithHeight.includes(tt.id) && (
              <Box px={1}>
                {expandedTagTypes.includes(tt.id) ? (
                  <Typography
                    onClick={() => setExpandedTagTypes((prev) => prev.filter((id) => id !== tt.id))}
                    variant='caption'
                    sx={{
                      cursor: 'pointer',
                      color: (theme) => theme.palette.primary.main,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    see less
                  </Typography>
                ) : (
                  <Typography
                    onClick={() => setExpandedTagTypes((prev) => [...prev, tt.id])}
                    variant='caption'
                    sx={{
                      cursor: 'pointer',
                      color: (theme) => theme.palette.primary.main,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    see more
                  </Typography>
                )}
              </Box>
            )}
            <Divider sx={{ mx: 1, pb: 1 }} />
          </Box>
        );
      })}
    </Box>
  );
};

const TagsSkeleton = () => {
  return (
    <Box p={1} display='flex' flexWrap='wrap'>
      <Skeleton
        key='tag-1'
        variant='rectangular'
        width={60}
        height={22}
        sx={{ mr: 1, mb: 1, borderRadius: 2 }}
      />
      <Skeleton
        key='tag-2'
        variant='rectangular'
        width={80}
        height={22}
        sx={{ mr: 1, mb: 1, borderRadius: 2 }}
      />
      <Skeleton
        key='tag-3'
        variant='rectangular'
        width={70}
        height={22}
        sx={{ mr: 1, mb: 1, borderRadius: 2 }}
      />
      <Skeleton
        key='tag-4'
        variant='rectangular'
        width={65}
        height={22}
        sx={{ mr: 1, mb: 1, borderRadius: 2 }}
      />
      <Skeleton
        key='tag-5'
        variant='rectangular'
        width={85}
        height={22}
        sx={{ mr: 1, mb: 1, borderRadius: 2 }}
      />
    </Box>
  );
};

const TagTypesSkeleton = () => {
  return (
    <Box>
      {Array.from({ length: 3 }).map((_, typeIndex) => (
        <Box key={typeIndex} sx={{ my: 1, pb: 1 }}>
          <Box px={1} mb={1}>
            <Skeleton variant='text' width={120} height={24} />
          </Box>
          <TagsSkeleton />
          <Divider sx={{ mx: 1, pb: 1 }} />
        </Box>
      ))}
    </Box>
  );
};
